import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import config from "../config/config";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { buildAuthHeaders, getStoredIdentity } from "../utils/authSession";

const FILTERS = ["all", "hard", "soft", "language", "other"];

const normalizeCategory = (cat) => {
  const c = String(cat || "").toLowerCase();
  if (c === "hard" || c === "soft" || c === "language") return c;
  return "other";
};

const CVSkillsBox = ({ userid }) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [catalog, setCatalog] = useState([]);
  const [skills, setSkills] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [lastRemoved, setLastRemoved] = useState(null);

  const activeUserid = String(userid || getStoredIdentity().userid || "").trim();

  const authHeaders = useMemo(() => {
    return buildAuthHeaders();
  }, []);

  const loadSkills = useCallback(async () => {
    if (!activeUserid) return;
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/cv/skills`, {
        headers: authHeaders,
        params: { userid: activeUserid },
      });
      const normalized = (res.data || []).map((s) => ({
        skill_select_id: Number(s.skill_select_id),
        skill: s.skill,
        category: normalizeCategory(s.category),
        rating: Number(s.rating),
      }));
      setSkills(normalized);
    } catch (e) {
      setError(e.response?.data?.message || "Skills konnten nicht geladen werden.");
    }
  }, [activeUserid, authHeaders]);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${config.API_BASE_URL}/api/cv/skills/select`, {
          headers: authHeaders,
          params: { q: query, limit: 20 },
        });
        setCatalog(res.data || []);
      } catch {
        setCatalog([]);
      }
    }, 220);

    return () => clearTimeout(t);
  }, [query, authHeaders]);

  const skillIds = useMemo(() => new Set(skills.map((s) => s.skill_select_id)), [skills]);

  const filteredSkills = useMemo(() => {
    if (filter === "all") return skills;
    return skills.filter((s) => normalizeCategory(s.category) === filter);
  }, [skills, filter]);

  const validationError = useMemo(() => {
    const missingRating = skills.find((s) => !Number.isInteger(s.rating) || s.rating < 1 || s.rating > 6);
    if (missingRating) return "Jeder Skill braucht eine Bewertung von 1 bis 6.";
    return "";
  }, [skills]);

  const addSkill = (entry) => {
    const id = Number(entry.id);
    if (!id || skillIds.has(id)) return;

    setSaved(false);
    setError("");
    setSkills((prev) => [
      ...prev,
      {
        skill_select_id: id,
        skill: entry.skill,
        category: normalizeCategory(entry.category),
        rating: 3,
      },
    ]);
    setQuery("");
  };

  const removeSkill = (skillId) => {
    const removed = skills.find((s) => s.skill_select_id === skillId);
    if (!removed) return;

    setSaved(false);
    setLastRemoved(removed);
    setSkills((prev) => prev.filter((s) => s.skill_select_id !== skillId));
  };

  const undoRemove = () => {
    if (!lastRemoved || skillIds.has(lastRemoved.skill_select_id)) return;
    setSkills((prev) => [...prev, lastRemoved]);
    setLastRemoved(null);
  };

  const setRating = (skillId, rating) => {
    setSaved(false);
    setSkills((prev) =>
      prev.map((s) => (s.skill_select_id === skillId ? { ...s, rating } : s))
    );
  };

  const saveSkills = async () => {
    if (!activeUserid) {
      setError("Keine Applicant-ID gefunden.");
      return;
    }
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError("");
      const payload = {
        userid: activeUserid,
        skills: skills.map((s) => ({ skill_select_id: s.skill_select_id, rating: s.rating })),
      };
      const res = await axios.put(`${config.API_BASE_URL}/api/cv/skills`, payload, {
        headers: authHeaders,
      });

      const normalized = (res.data?.skills || []).map((s) => ({
        skill_select_id: Number(s.skill_select_id),
        skill: s.skill,
        category: normalizeCategory(s.category),
        rating: Number(s.rating),
      }));
      setSkills(normalized);
      setSaved(true);
      setLastRemoved(null);
    } catch (e) {
      setError(e.response?.data?.message || "Skills konnten nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="applicant-tool-card applicant-tool-card--skills">
      <div className="applicant-tool-card__head">
        <div>
          <span className="applicant-section-kicker">Skillbereich</span>
          <h4>Deine wichtigen Skills</h4>
        </div>
        <Button onClick={saveSkills} disabled={saving} className="applicant-cta-button">
          {saving ? "Speichert..." : "Skills speichern"}
        </Button>
      </div>

      <div className="applicant-tool-card__intro">
        Suche Skills, fuege sie hinzu und bewerte sie ehrlich. Klare Skills machen deine Bewerbung fuer Firmen lesbarer.
      </div>

      <div className="applicant-tool-card__body">
        <div className="applicant-skills-toolbar">
          <div>
            <Input
              placeholder="Skill suchen ..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="applicant-field"
            />
            <div className="applicant-skills-catalog">
              {catalog.length === 0 ? (
                <p>Kein passender Skill gefunden. Versuche ein anderes Wort.</p>
              ) : (
                <div className="applicant-tag-cloud">
                  {catalog.map((entry) => {
                    const disabled = skillIds.has(Number(entry.id));
                    return (
                      <Button
                        key={entry.id}
                        size="sm"
                        variant="outline"
                        disabled={disabled}
                        className="applicant-chip-button"
                        onClick={() => addSkill(entry)}
                      >
                        {entry.skill}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="applicant-tablist applicant-tablist-secondary">
            {FILTERS.map((f) => (
              <TabsTrigger key={f} value={f} className="applicant-tabtrigger applicant-tabtrigger--compact">
                {f === "all" ? "Alle" : f === "hard" ? "Fachlich" : f === "soft" ? "Soft Skills" : f === "language" ? "Sprache" : "Weitere"}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {lastRemoved ? (
          <div className="applicant-inline-alert applicant-inline-alert-warning">
            <span>Entfernt: {lastRemoved.skill}</span>
            <Button size="sm" variant="outline" className="applicant-chip-button" onClick={undoRemove}>
              Rueckgaengig
            </Button>
          </div>
        ) : null}

        {filteredSkills.length === 0 ? (
          <div className="applicant-empty-card">
            In diesem Bereich liegt noch kein Skill. Fuege oben passende Skills hinzu.
          </div>
        ) : (
          <div className="applicant-skills-list">
            {filteredSkills.map((item) => (
              <div key={item.skill_select_id} className="applicant-skill-row">
                <div className="applicant-skill-row__head">
                  <div className="applicant-skill-row__copy">
                    <span className={`applicant-skill-badge is-${item.category}`}>{item.category === "hard" ? "Fachlich" : item.category === "soft" ? "Soft Skill" : item.category === "language" ? "Sprache" : "Weiteres"}</span>
                    <strong>{item.skill}</strong>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="applicant-chip-button applicant-chip-button-danger"
                    onClick={() => removeSkill(item.skill_select_id)}
                  >
                    Entfernen
                  </Button>
                </div>

                <div className="applicant-skill-row__rating">
                  <span>Staerke</span>
                  {[1, 2, 3, 4, 5, 6].map((value) => (
                    <Button
                      key={value}
                      size="sm"
                      type="button"
                      onClick={() => setRating(item.skill_select_id, value)}
                      className={
                        item.rating === value
                          ? "applicant-rating-button is-active"
                          : "applicant-rating-button"
                      }
                    >
                      {value}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {error ? <p className="applicant-feedback-text is-error">{error}</p> : null}
        {!error && validationError ? <p className="applicant-feedback-text is-warning">{validationError}</p> : null}
        {!error && saved ? <p className="applicant-feedback-text is-success">Skills wurden gespeichert.</p> : null}
      </div>
    </div>
  );
};

export default CVSkillsBox;
